/**
 * Vuex Stars Module
 *
 * Manages stars state, caching, and optimistic updates.
 * Integrates with starsService for API calls.
 */

import starsService from '@/services/starsService';

const starsModule = {
  namespaced: true,

  state: {
    // User's starred items cache
    userStars: {
      places: [],
      articles: [],
      total: 0,
      stats: null,
      loading: false,
      error: null,
      cachedAt: null
    },

    // Item star data cache (by item type and ID)
    itemStars: {
      // Format: { 'place:paris-france': { count: 42, userStarred: true, cachedAt: ... } }
    },

    // Leaderboards cache
    leaderboards: {
      topPlaces: [],
      topArticles: [],
      topUsers: [],
      loading: false,
      error: null,
      cachedAt: null
    },

    // Cache duration in milliseconds (5 minutes)
    cacheDuration: 5 * 60 * 1000
  },

  mutations: {
    // User stars mutations
    SET_USER_STARS(state, data) {
      state.userStars.places = data.starred_places || [];
      state.userStars.articles = data.starred_articles || [];
      state.userStars.total = data.total_stars || 0;
      state.userStars.stats = data.stats || null;
      state.userStars.cachedAt = Date.now();
    },

    SET_USER_STARS_LOADING(state, loading) {
      state.userStars.loading = loading;
    },

    SET_USER_STARS_ERROR(state, error) {
      state.userStars.error = error;
    },

    // Add/remove item from user's stars (optimistic update)
    ADD_USER_STAR(state, { itemId, itemType, itemData }) {
      const listKey = itemType === 'place' ? 'places' : 'articles';
      const exists = state.userStars[listKey].find(item => item._id === itemId);

      if (!exists) {
        state.userStars[listKey].unshift({
          _id: itemId,
          ...itemData,
          starred_at: new Date().toISOString()
        });
        state.userStars.total += 1;
      }
    },

    REMOVE_USER_STAR(state, { itemId, itemType }) {
      const listKey = itemType === 'place' ? 'places' : 'articles';
      const index = state.userStars[listKey].findIndex(item => item._id === itemId);

      if (index !== -1) {
        state.userStars[listKey].splice(index, 1);
        state.userStars.total = Math.max(0, state.userStars.total - 1);
      }
    },

    // Item star data mutations
    SET_ITEM_STARS(state, { itemId, itemType, data }) {
      const key = `${itemType}:${itemId}`;
      state.itemStars[key] = {
        ...data,
        cachedAt: Date.now()
      };
    },

    UPDATE_ITEM_STAR_COUNT(state, { itemId, itemType, increment }) {
      const key = `${itemType}:${itemId}`;
      if (state.itemStars[key]) {
        state.itemStars[key].star_count += increment;
        state.itemStars[key].user_starred = increment > 0;
      }
    },

    // Leaderboards mutations
    SET_LEADERBOARDS(state, data) {
      state.leaderboards.topPlaces = data.top_places || [];
      state.leaderboards.topArticles = data.top_articles || [];
      state.leaderboards.topUsers = data.top_users || [];
      state.leaderboards.cachedAt = Date.now();
    },

    SET_LEADERBOARDS_LOADING(state, loading) {
      state.leaderboards.loading = loading;
    },

    SET_LEADERBOARDS_ERROR(state, error) {
      state.leaderboards.error = error;
    },

    // Clear cache
    CLEAR_STARS_CACHE(state) {
      state.userStars.cachedAt = null;
      state.leaderboards.cachedAt = null;
      state.itemStars = {};
    }
  },

  actions: {
    /**
     * Toggle star for an item (optimistic update)
     */
    async toggleStar({ commit, rootGetters }, { itemId, itemType, itemData }) {
      try {
        // Check authentication
        if (!rootGetters['auth/isAuthenticated']) {
          throw new Error('Authentication required');
        }

        // Get current star status
        const key = `${itemType}:${itemId}`;
        const currentStatus = this.state.stars.itemStars[key];
        const wasStarred = currentStatus?.user_starred || false;

        // Optimistic update: Update UI immediately
        commit('UPDATE_ITEM_STAR_COUNT', {
          itemId,
          itemType,
          increment: wasStarred ? -1 : 1
        });

        // Update user's stars list optimistically
        if (wasStarred) {
          commit('REMOVE_USER_STAR', { itemId, itemType });
        } else {
          commit('ADD_USER_STAR', { itemId, itemType, itemData });
        }

        // Make API call in background
        const response = await starsService.toggleStar(itemId, itemType);

        // Update with actual data from server
        commit('SET_ITEM_STARS', {
          itemId,
          itemType,
          data: {
            star_count: response.data.star_count,
            user_starred: response.data.status === 'starred',
            starred_at: response.data.starred_at
          }
        });

        return {
          success: true,
          action: response.data.status,
          starCount: response.data.star_count
        };

      } catch (error) {
        console.error('Error toggling star:', error);

        // Revert optimistic update on error
        const key = `${itemType}:${itemId}`;
        const currentStatus = this.state.stars.itemStars[key];
        const wasStarred = currentStatus?.user_starred || false;

        commit('UPDATE_ITEM_STAR_COUNT', {
          itemId,
          itemType,
          increment: wasStarred ? -1 : 1
        });

        throw error;
      }
    },

    /**
     * Fetch star data for an item
     */
    async fetchItemStars({ commit, state }, { itemId, itemType, forceRefresh = false }) {
      try {
        const key = `${itemType}:${itemId}`;

        // Check cache
        if (!forceRefresh && state.itemStars[key]) {
          const cacheAge = Date.now() - state.itemStars[key].cachedAt;
          if (cacheAge < state.cacheDuration) {
            return { success: true, cached: true };
          }
        }

        // Fetch from API
        let data;
        if (itemType === 'place') {
          data = await starsService.getPlaceStars(itemId);
        } else {
          data = await starsService.getArticleStars(itemId);
        }

        commit('SET_ITEM_STARS', { itemId, itemType, data });

        return { success: true, cached: false };

      } catch (error) {
        console.error('Error fetching item stars:', error);
        throw error;
      }
    },

    /**
     * Fetch user's starred items
     */
    async fetchUserStars({ commit, state, rootGetters }, { forceRefresh = false } = {}) {
      try {
        // Check authentication
        if (!rootGetters['auth/isAuthenticated']) {
          commit('SET_USER_STARS_ERROR', 'Authentication required');
          return { success: false };
        }

        // Check cache
        if (!forceRefresh && state.userStars.cachedAt) {
          const cacheAge = Date.now() - state.userStars.cachedAt;
          if (cacheAge < state.cacheDuration) {
            return { success: true, cached: true };
          }
        }

        commit('SET_USER_STARS_LOADING', true);
        commit('SET_USER_STARS_ERROR', null);

        const data = await starsService.getUserStars('me');

        commit('SET_USER_STARS', data);
        commit('SET_USER_STARS_LOADING', false);

        return { success: true, cached: false };

      } catch (error) {
        commit('SET_USER_STARS_ERROR', error.message);
        commit('SET_USER_STARS_LOADING', false);
        console.error('Error fetching user stars:', error);
        throw error;
      }
    },

    /**
     * Fetch star leaderboards
     */
    async fetchLeaderboards({ commit, state }, { forceRefresh = false, ...options } = {}) {
      try {
        // Check cache
        if (!forceRefresh && state.leaderboards.cachedAt) {
          const cacheAge = Date.now() - state.leaderboards.cachedAt;
          if (cacheAge < state.cacheDuration) {
            return { success: true, cached: true };
          }
        }

        commit('SET_LEADERBOARDS_LOADING', true);
        commit('SET_LEADERBOARDS_ERROR', null);

        const data = await starsService.getStarLeaderboards(options);

        commit('SET_LEADERBOARDS', data);
        commit('SET_LEADERBOARDS_LOADING', false);

        return { success: true, cached: false };

      } catch (error) {
        commit('SET_LEADERBOARDS_ERROR', error.message);
        commit('SET_LEADERBOARDS_LOADING', false);
        console.error('Error fetching leaderboards:', error);
        throw error;
      }
    },

    /**
     * Clear all stars cache
     */
    clearCache({ commit }) {
      commit('CLEAR_STARS_CACHE');
    }
  },

  getters: {
    // User stars getters
    userStarredPlaces: state => state.userStars.places,
    userStarredArticles: state => state.userStars.articles,
    userTotalStars: state => state.userStars.total,
    userStarsLoading: state => state.userStars.loading,
    userStarsError: state => state.userStars.error,

    // Check if user has starred an item
    hasUserStarred: (state) => (itemId, itemType) => {
      const key = `${itemType}:${itemId}`;
      return state.itemStars[key]?.user_starred || false;
    },

    // Get star count for an item
    getStarCount: (state) => (itemId, itemType) => {
      const key = `${itemType}:${itemId}`;
      return state.itemStars[key]?.star_count || 0;
    },

    // Get full star data for an item
    getItemStarData: (state) => (itemId, itemType) => {
      const key = `${itemType}:${itemId}`;
      return state.itemStars[key] || null;
    },

    // Leaderboards getters
    topStarredPlaces: state => state.leaderboards.topPlaces,
    topStarredArticles: state => state.leaderboards.topArticles,
    topStarUsers: state => state.leaderboards.topUsers,
    leaderboardsLoading: state => state.leaderboards.loading,
    leaderboardsError: state => state.leaderboards.error
  }
};

export default starsModule;
